import type { ArchitectureProject } from '@hyphen-db-studio/architecture-core';
import type { Project } from '@hyphen-db-studio/schema-core';

/**
 * Data used to live in this browser's localStorage. The server is now the source of truth, but anything a user
 * created before accounts existed is still here, so it can be offered for import once and then cleared.
 */
const LEGACY_KEYS = {
  er: 'hyphen-db-studio:projects:v1',
  architecture: 'hyphen-db-studio:architecture-projects:v1',
  organizations: 'hyphen-db-studio:organizations:v1',
  workspaceProjects: 'hyphen-db-studio:workspace-projects:v1',
} as const;

export interface LegacyOrganization {
  id: string;
  name: string;
}

export interface LegacyWorkspaceProject {
  id: string;
  orgId: string;
  name: string;
  description: string;
  erIds: string[];
  archIds: string[];
}

export interface LegacyData {
  organizations: LegacyOrganization[];
  workspaceProjects: LegacyWorkspaceProject[];
  er: Project[];
  architecture: ArchitectureProject[];
}

function read<T>(key: string): T[] {
  try {
    const parsed = JSON.parse(localStorage.getItem(key) ?? '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function loadLegacyData(): LegacyData {
  const er = read<Project>(LEGACY_KEYS.er);
  const architecture = read<ArchitectureProject>(LEGACY_KEYS.architecture);
  const organizations = read<LegacyOrganization>(LEGACY_KEYS.organizations);
  let workspaceProjects = read<LegacyWorkspaceProject>(LEGACY_KEYS.workspaceProjects);

  // Diagrams from before organizations existed were never grouped: collect them into a project of their own.
  const linkedEr = new Set(workspaceProjects.flatMap((p) => p.erIds));
  const linkedArch = new Set(workspaceProjects.flatMap((p) => p.archIds));
  const orphanEr = er.filter((d) => !linkedEr.has(d.id)).map((d) => d.id);
  const orphanArch = architecture.filter((d) => !linkedArch.has(d.id)).map((d) => d.id);
  if (orphanEr.length || orphanArch.length) {
    const orgs = organizations.length ? organizations : [{ id: 'legacy_org', name: 'My Organization' }];
    if (!organizations.length) organizations.push(orgs[0]);
    workspaceProjects = [
      ...workspaceProjects,
      { id: 'legacy_project', orgId: orgs[0].id, name: 'Imported Diagrams', description: '', erIds: orphanEr, archIds: orphanArch },
    ];
  }
  return { organizations, workspaceProjects, er, architecture };
}

export function hasLegacyData(): boolean {
  const data = loadLegacyData();
  return data.er.length > 0 || data.architecture.length > 0 || data.organizations.length > 0;
}

export function clearLegacyData(): void {
  try {
    Object.values(LEGACY_KEYS).forEach((key) => localStorage.removeItem(key));
  } catch {
    // ignore
  }
}
